import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { getApps, createApp, updateApp, deleteApp } from '../api/apps';
import type { AppRequest } from '../api/apps';
import { AppResponse } from '../types';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { PageHeader } from '../components/PageHeader';

const EMPTY: AppRequest = { name: '', slug: '', clientId: '', clientSecret: '', authUrl: '', tokenUrl: '', scopes: '' };

export function AppsPage() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<AppResponse | null>(null);
  const [form, setForm] = useState<AppRequest>(EMPTY);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: apps = [] } = useQuery({ queryKey: ['apps'], queryFn: getApps });

  const saveMutation = useMutation({
    mutationFn: () => editing ? updateApp(editing.id, form) : createApp(form),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['apps'] });
      setShowForm(false);
      setEditing(null);
      setForm(EMPTY);
      setError(null);
    },
    onError: (err: unknown) => {
      const status = (err as { response?: { status?: number } })?.response?.status;
      setError(status === 409 ? `Slug '${form.slug}' already in use.` : 'Save failed.');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteApp,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['apps'] }),
  });

  function openCreate() { setEditing(null); setForm(EMPTY); setShowForm(true); setError(null); }
  function openEdit(app: AppResponse) {
    setEditing(app);
    setForm({ name: app.name, slug: app.slug, clientId: app.clientId, clientSecret: '', authUrl: app.authUrl, tokenUrl: app.tokenUrl, scopes: app.scopes });
    setShowForm(true);
    setError(null);
  }

  function Field({
    fieldKey,
    label,
    type = 'text',
  }: {
    fieldKey: keyof AppRequest;
    label: string;
    type?: string;
  }) {
    return (
      <div className="space-y-1">
        <Label htmlFor={`field-${fieldKey}`} className="text-xs text-muted-foreground">{label}</Label>
        <Input
          id={`field-${fieldKey}`}
          type={type}
          className="h-9"
          value={(form[fieldKey] as string) ?? ''}
          onChange={e => setForm(f => ({ ...f, [fieldKey]: e.target.value }))}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="OAuth apps"
        description="OAuth clients that flows use to authorize third-party tools."
        actions={
          <Button size="sm" onClick={openCreate}>
            <Plus className="h-4 w-4" />
            New app
          </Button>
        }
      />

      {showForm && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold">{editing ? 'Edit app' : 'New app'}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {error && (
              <p className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-md px-3 py-2">
                {error}
              </p>
            )}
            <div className="grid grid-cols-2 gap-3">
              <Field fieldKey="name" label="Name" />
              <Field fieldKey="slug" label="Slug (e.g. asana)" />
              <Field fieldKey="clientId" label="Client ID" />
              <Field fieldKey="clientSecret" label={editing ? 'Client secret (leave blank to keep)' : 'Client secret'} type="password" />
              <Field fieldKey="authUrl" label="Auth URL" />
              <Field fieldKey="tokenUrl" label="Token URL" />
            </div>
            <Field fieldKey="scopes" label="Scopes (space-separated)" />
            <div className="flex gap-2">
              <Button size="sm" onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
                {saveMutation.isPending ? 'Saving…' : 'Save'}
              </Button>
              <Button variant="outline" size="sm" onClick={() => { setShowForm(false); setEditing(null); }}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="space-y-2">
        {apps.map(app => (
          <div key={app.id} className="bg-card border border-border rounded-lg p-4 flex items-center justify-between text-sm">
            <div>
              <div className="font-semibold">
                {app.name} <span className="text-muted-foreground font-normal">({app.slug})</span>
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">Redirect: {app.redirectUri}</div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="xs" onClick={() => openEdit(app)}>Edit</Button>
              <Button
                variant="ghost"
                size="xs"
                className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                onClick={() => { if (window.confirm(`Delete ${app.name}?`)) deleteMutation.mutate(app.id); }}
              >
                Delete
              </Button>
            </div>
          </div>
        ))}
        {apps.length === 0 && <p className="text-muted-foreground text-sm">No apps configured.</p>}
      </div>
    </div>
  );
}
